import React from "react";
import { Divider, Checkbox, Form, Input, Button, Alert, Select, Icon, Typography } from "antd";
import { useSignupContext } from "../../../context/SignupContext";
import { useTranslation } from "react-i18next";
import { countries, commonCountries } from "../../../utils/countryList";
import { signupSubmit } from "../../../api/signupSubmitAPI";
import { navigate } from "@reach/router";
import { steps } from "../../../utils/steps";
import { updateProgressStep } from "../../../actions/appActions";
import { useAppContext } from "../../../context/AppContext";
import { EXPIRY_MONTHS, EXPIRY_YEARS, index } from "../../../utils/constants";

const { Paragraph } = Typography;
const { Option, OptGroup } = Select;

const cvvValidator = (rule, value, callback) => {
    if (!value) {
        callback("Please input your card's CVV");
    } else if (isNaN(value)) {
        callback("Please use a valid CVV (3/4 digits)");
    }
    callback();
};

export const BillingForm = props => {
    const {
        state: { errors },
        dispatch: signupDispatch,
    } = useSignupContext();
    const { dispatch: appDispatch, state: { cart }, } = useAppContext();
    const { signupParams } = props;
    const { items = [] } = cart || {};

    const { t } = useTranslation();

    if (Object.entries(signupParams).length === 0 && signupParams.constructor === Object) {
        navigate(steps[1].url); // prevent billing page from being submitted without username, password etc
    }

    const handleSubmit = e => {
        e.preventDefault();
        if (!(cart && items.length > 0)) {
            navigate(index);
        }
        props.form.validateFieldsAndScroll(async (err, fieldsValue) => {
            if (!err) {
                let ccode;
                let sameShipping = fieldsValue['shipping-sameas-billing'];
                for (let i = 0; i < countries.length; i++) {
                    let currentC = countries[i];
                    if (currentC.name === fieldsValue["country"]) {
                        ccode = currentC.code;
                        break;
                    }
                }
                const signupResult = await signupSubmit(
                    {
                        ...signupParams,
                        firstName: fieldsValue["firstName"],
                        lastName: fieldsValue["lastName"],
                        vat: fieldsValue['vat'],
                        creditCard: {
                            cardNumber: fieldsValue["card"],
                            cvv: fieldsValue["cvv"],
                            expireMonth: fieldsValue["expirationMonth"],
                            expireYear: fieldsValue["expirationYear"],
                        },
                        billingAddress: {
                            address: fieldsValue["address-1"],
                            address2: fieldsValue["address-2"],
                            city: fieldsValue["city"],
                            state: fieldsValue["state"],
                            country: fieldsValue["country"],
                            countryCode: ccode,
                            zipCode: fieldsValue["zip-code"],
                        },
                        shippingAddress: !sameShipping ? {
                            address: fieldsValue["sh-address-1"],
                            address2: fieldsValue["sh-address-2"],
                            city: fieldsValue["sh-city"],
                            state: fieldsValue["sh-state"],
                            country: fieldsValue["sh-country"],
                            countryCode: "", // the country code isn't currently used for shipping address
                            zipCode: fieldsValue["sh-zip-code"],
                        } : null,
                    },
                    signupDispatch
                );
                if (signupResult) {
                    navigate(steps[3].url); //proceed if signup success
                    appDispatch(updateProgressStep(3));
                }
            }
        });
    };
    const { getFieldDecorator } = props.form;

    const formItemLayout = {
        labelCol: {
            xs: { span: 24 },
        },
        wrapperCol: {
            xs: { span: 24 },
        },
    };

    return (
        <>
            <Form onSubmit={e => handleSubmit(e)} style={{ textAlign: "left" }}>
                <Form.Item
                    style={{
                        display: "inline-block",
                        width: "calc(50% - 12px)",
                    }}
                    label="First Name"
                >
                    {getFieldDecorator("firstName", {
                        rules: [
                            {
                                required: true,
                                message: "Please fill your first name",
                            },
                        ],
                        initialValue: signupParams.firstName,
                    })(<Input />)}
                </Form.Item>
                <span style={{ display: "inline-block", width: "24px" }} />
                <Form.Item
                    style={{
                        display: "inline-block",
                        width: "calc(50% - 12px)",
                    }}
                    label="Last Name"
                >
                    {getFieldDecorator("lastName", {
                        rules: [
                            {
                                required: true,
                                message: "Please input your last name",
                            },
                        ],
                        initialValue: signupParams.lastName,
                    })(<Input />)}
                </Form.Item>

                <Form.Item
                    style={{
                        display: "inline-block",
                        width: "calc(50% - 12px)",
                    }}
                    label="Card Number"
                >
                    {getFieldDecorator("card", {
                        rules: [
                            {
                                required: true,
                                message: "Please input your credit card number",
                            },
                        ],
                    })(<Input suffix={<Icon type="credit-card" />} />)}
                </Form.Item>
                <span style={{ display: "inline-block", width: "24px" }} />
                <Form.Item
                    style={{
                        display: "inline-block",
                        width: "calc(50% - 12px)",
                    }}
                    label="CVV(3 or 4 Digits)"
                >
                    {getFieldDecorator("cvv", {
                        rules: [
                            {
                                required: true,
                                validator: cvvValidator,
                            },
                        ],
                    })(<Input />)}
                </Form.Item>
                <Form.Item {...formItemLayout} style={{ marginBottom: 0, textAlign: "left" }}>
                    <Form.Item
                        style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                        label="Expiration Month"
                    >
                        {getFieldDecorator("expirationMonth", {
                            rules: [
                                {
                                    required: true,
                                },
                            ],
                            initialValue: signupParams["expirationMonth"],
                        })(
                            <Select>
                                {EXPIRY_MONTHS.map((value, index) => (
                                    <Option value={value} key={"month" + index}>
                                        {value}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                    <span style={{ display: "inline-block", width: "24px" }} />
                    <Form.Item
                        style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                        label="Expiration Year"
                    >
                        {getFieldDecorator("expirationYear", {
                            rules: [
                                {
                                    required: true,
                                },
                            ],
                            initialValue: signupParams["expirationYear"],
                        })(
                            <Select>
                                {EXPIRY_YEARS.map((value, index) => (
                                    <Option value={value} key={"year" + index}>
                                        {value}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                </Form.Item>
                <Form.Item {...formItemLayout} label="Address 1">
                    {getFieldDecorator("address-1", {
                        rules: [
                            {
                                required: true,
                                message: "Please input your billing address",
                            },
                        ],
                        initialValue: signupParams["address-1"],
                    })(<Input suffix={<Icon type="home" />} />)}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Address 2">
                    {getFieldDecorator("address-2", {
                        initialValue: signupParams["address-2"],
                    })(<Input />)}
                </Form.Item>

                {/* state and city */}
                <Form.Item {...formItemLayout} style={{ marginBottom: 0, textAlign: "left" }}>
                    <Form.Item
                        style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                        label="Province / State"
                    >
                        {getFieldDecorator("state", {
                            rules: [
                                {
                                    required: true,
                                    message: "Please specify your billing state",
                                },
                            ],
                            initialValue: signupParams.state,
                        })(<Input />)}
                    </Form.Item>
                    <span style={{ display: "inline-block", width: "24px" }} />
                    <Form.Item
                        style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                        label="City"
                    >
                        {getFieldDecorator("city", {
                            rules: [
                                {
                                    required: true,
                                    message: "Please specify your billing city",
                                },
                            ],
                            initialValue: signupParams.city,
                        })(<Input />)}
                    </Form.Item>
                </Form.Item>
                {/* postal code and country */}
                <Form.Item {...formItemLayout} style={{ marginBottom: 0, textAlign: "left" }}>
                    <Form.Item
                        style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                        label="Zip / Postal Code"
                    >
                        {getFieldDecorator("zip-code", {
                            rules: [
                                {
                                    required: true,
                                    message: "Please specify your billing zip or postal code",
                                },
                            ],
                            initialValue: signupParams["zip-code"],
                        })(<Input />)}
                    </Form.Item>
                    <span style={{ display: "inline-block", width: "24px" }} />
                    <Form.Item
                        style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                        label="Country"
                    >
                        {getFieldDecorator("country", {
                            rules: [
                                {
                                    required: true,
                                    message: "Please specify your billing country",
                                },
                            ],
                            initialValue: signupParams["country"],
                            /**
                             * remove the hard-coded prefix (comCountry || allCountry)
                             */
                            getValueFromEvent: val => val.slice(10),
                        })(
                            <Select>
                                {
                                    /**
                                     * In order to display duplicate select option ('canada' appeared twice in the list)
                                     * we need to hard-code the prefix to country, because the ui component require each
                                     * select item have unique value.
                                     */
                                    <OptGroup label="Commonly Used">
                                        {commonCountries.map((commonCountry, key) => (
                                            <Option
                                                value={"comCountry" + commonCountry.name}
                                                key={key}
                                            >
                                                {commonCountry.name}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                }
                                {
                                    <OptGroup label="Alphabetical Order">
                                        {countries.map((country, key) => (
                                            <Option value={"allCountry" + country.name} key={key}>
                                                {country.name}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                }
                            </Select>
                        )}
                    </Form.Item>
                </Form.Item>

                <Divider dashed style={{ margin: "1rem 0" }} />

                <Paragraph type="secondary" style={{ textAlign: "center" }}>
                    For invoice and tax purposes only.
                </Paragraph>
                <Form.Item label="VAT">
                    {getFieldDecorator("vat", {
                        initialValue: signupParams.vat,
                    })(<Input placeholder="Only applies to customers from the European Union" />)}
                </Form.Item>
                <Form.Item {...formItemLayout}>
                    {getFieldDecorator("shipping-sameas-billing", {
                        valuePropName: "checked",
                        initialValue: signupParams["shipping-sameas-billing"],
                    })(
                        <Checkbox style={{ lineHeight: "1.5rem" }}>
                            Shipping address same as billing
                        </Checkbox>
                    )}
                </Form.Item>
                <span style={{ display: signupParams["shipping-sameas-billing"] ? "none" : "block" }}>
                    <Form.Item {...formItemLayout} label="Shipping Address 1">
                        {getFieldDecorator("sh-address-1", {
                            initialValue: signupParams["sh-address-1"],
                        })(<Input suffix={<Icon type="home" />} />)}
                    </Form.Item>
                    <Form.Item {...formItemLayout} label="Shipping Address 2">
                        {getFieldDecorator("sh-address-2", {
                            initialValue: signupParams["sh-address-2"],
                        })(<Input />)}
                    </Form.Item>

                    {/* state and city */}
                    <Form.Item {...formItemLayout} style={{ marginBottom: 0, textAlign: "left" }}>
                        <Form.Item
                            style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                            label="Shipping Province / State"
                        >
                            {getFieldDecorator("sh-state", {
                                initialValue: signupParams["sh-state"],
                            })(<Input />)}
                        </Form.Item>
                        <span style={{ display: "inline-block", width: "24px" }} />
                        <Form.Item
                            style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                            label="Shipping City"
                        >
                            {getFieldDecorator("sh-city", {
                                initialValue: signupParams["sh-city"],
                            })(<Input />)}
                        </Form.Item>
                    </Form.Item>
                    {/* postal code and country */}
                    <Form.Item {...formItemLayout} style={{ marginBottom: 0, textAlign: "left" }}>
                        <Form.Item
                            style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                            label="Shipping Zip / Postal Code"
                        >
                            {getFieldDecorator("sh-zip-code", {
                                initialValue: signupParams["sh-zip-code"],
                            })(<Input />)}
                        </Form.Item>
                        <span style={{ display: "inline-block", width: "24px" }} />
                        <Form.Item
                            style={{ display: "inline-block", width: "calc(50% - 12px)" }}
                            label="Shipping Country"
                        >
                            {getFieldDecorator("sh-country", {
                                initialValue: signupParams["sh-country"],
                                /**
                                 * remove the hard-coded prefix (comCountry || allCountry)
                                 */
                                getValueFromEvent: val => val.slice(10),
                            })(
                                <Select>
                                    {
                                        /**
                                         * In order to display duplicate select option ('canada' appeared twice in the list)
                                         * we need to hard-code the prefix to country, because the ui component require each
                                         * select item have unique value.
                                         */
                                        <OptGroup label="Commonly Used">
                                            {commonCountries.map((commonCountry, key) => (
                                                <Option
                                                    value={"comCountry" + commonCountry.name}
                                                    key={key}
                                                >
                                                    {commonCountry.name}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    }
                                    {
                                        <OptGroup label="Alphabetical Order">
                                            {countries.map((country, key) => (
                                                <Option value={"allCountry" + country.name} key={key}>
                                                    {country.name}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    }
                                </Select>
                            )}
                        </Form.Item>
                    </Form.Item>
                </span>
                {/*
                    if there is an error sending the activation email, there is no point in trying to submit
                    the signup request again. it will fail with a different error (that the account already exists)
                */}
                {!(
                    errors.signup &&
                    errors.signup.messageId === "signup.error.activation_email_send"
                ) && (
                        <Form.Item style={{ textAlign: "center" }}>
                            <Button type="primary" size="large" htmlType="submit">
                                {`Submit`}
                            </Button>
                        </Form.Item>
                    )}

                {errors.signup && (
                    <Form.Item style={{ textAlign: "center" }}>
                        <Alert
                            type="error"
                            showIcon
                            message={
                                `${t(errors.signup.messageId || "signup.error.unknown_error")} ` +
                                `[Error code: ${errors.signup.errorCode || "N/A"}]` +
                                (errors.signup.caseId ? ` [Case ID: ${errors.signup.caseId}]` : "")
                            }
                        />
                    </Form.Item>
                )}
            </Form>
        </>
    );
};

const WrappedBillingForm = Form.create({
    name: "billing",
    onFieldsChange(props, changedFields) {
        const fieldsValue = props.form.getFieldsValue();
        const { updatePreview } = props;
        //submit signup params to get a preview update for tax purpose
        if (
            fieldsValue["address-1"] &&
            fieldsValue["state"] &&
            fieldsValue["city"] &&
            fieldsValue["country"]
        ) {
            // update the preview so we get the updated tax amount
            updatePreview();
        }
        // update the changed signupParams to context store
        let changedParams = {};
        Object.keys(changedFields).forEach(fieldName => {
            changedParams[fieldName] = changedFields[fieldName].value;
        });
        const { onFormsChange } = props;
        onFormsChange(changedParams);
    },
})(BillingForm);

export default WrappedBillingForm;
